import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// ─── Role Alignment Questionnaire ──────────────────────────────────────────────
// A single SCHEMA drives both the fillable form (below) and the read-only
// results view (RoleAlignmentResults), so the two can never drift. Submissions
// are stored as a flat { fieldName: value } object — values are strings, arrays
// (multi-select / inventory), or small objects (ratings, lanes).

const brand = {
  navy: '#002040', orange: '#F09010', blue: '#0060A0',
  ltBlue: '#E8F0F8', medGray: '#E0E0E0', text: '#333', textLight: '#666',
};

const RUNGS_V1 = [
  'Advisor Track: Client Service Associate',
  'Advisor Track: Paraplanner',
  'Advisor Track: Advisor',
  'Advisor Track: Lead Advisor',
  'Advisor Track: Practicing Partner',
  'Operations Track: Client Service Administrator',
  'Operations Track: Senior Client Service Administrator',
  'Operations Track: Manager',
  'Operations Track: Director',
  'Operations Track: Officer',
];

const TECH_SYSTEMS_V1 = [
  'Claude', 'Salesforce', 'Orion', 'HubSpot', 'eMoney', 'Holistiplan', 'Drake',
  'Schwab', 'Fidelity', 'FireLight', 'Zoom', 'Teams', 'Box', 'OneDrive', 'SharePoint', 'NetSuite',
];

// Order matches the original three-column grid (row-major).
const LICENSES_V1 = [
  'Series 6', 'Life & Health', 'CFP®',
  'Series 7', 'Property & Casualty', 'RICP',
  'Series 63', 'CPA', 'ChFC',
  'Series 65', 'EA', 'CLU',
  'Series 66', 'Notary', 'FPQP',
];

const LANES_V1 = [
  { lane: 'Financial Services', subs: [
    'Frontstage Advisory (Rung 3-4 Advisors and Principals)',
    'Frontstage Advisory (Rung 5 Partners)',
    'Backstage Advisory Support (CSA / Paraplanner)',
    'Transactional Services (Tax Prep, Tax Planning, Insurance, Annuity, Social Security)',
  ] },
  { lane: 'Advisor Experience', subs: [
    'Operations', 'Onboarding and Transitions', 'B2B Sales Support', 'Advanced Planning and B2B Life Sales',
  ] },
  { lane: 'Advisor Success', subs: [
    'Relationship Management', 'Learning', 'Events', 'Integration', 'Ecosystem Onboarding',
  ] },
  { lane: 'Finance', subs: ['Accounting', 'Treasury', 'Tax'] },
  { lane: 'Marketing', subs: ['B2C Marketing', 'Agency Marketing', 'B2B Marketing'] },
  { lane: 'Business Development', subs: ['B2B (External)', 'B2C (External)', 'B2B (Internal)', 'M&A Sales'] },
  { lane: 'Compliance', subs: ['Supervision', 'Compliance'] },
  { lane: 'Technology', subs: ['IT', 'FinTech'] },
  { lane: 'People', subs: ['Human Resources', 'Talent Development', 'People and Culture'] },
  { lane: 'Strategic Execution', subs: ['Executive Support', 'Legal and Governance Execution'] },
];


// ── v2 option lists (2026-08-20) ─────────────────────────────────────────────
// v1's lists are frozen above under _V1 names and referenced ONLY by
// ROLE_ALIGNMENT_SCHEMA_V1, so submissions already on file keep rendering with
// the exact options they were offered. Never edit a _V1 list.

// v2 renames "Track" to "Path" throughout, matching the Career Path Guide.
const RUNGS = RUNGS_V1.map((r) => r.replace('Track:', 'Path:'));

// v2 drops Teams/Box/SharePoint and adds AI agents, Slack and the Microsoft
// suite broken out individually. 'Other' pairs with the tech_other text field
// below it — the ratings block renders rows, not inputs, so the free-text name
// is captured as its own field rather than inside the table.
const TECH_SYSTEMS = [
  'Claude', 'AI agents', 'Salesforce', 'Orion', 'HubSpot', 'eMoney', 'Holistiplan',
  'Drake', 'Schwab', 'Fidelity', 'FireLight', 'Zoom', 'Slack', 'OneDrive',
  'NetSuite', 'Microsoft', 'Outlook', 'Word', 'Excel', 'PowerPoint', 'Other',
];

const LICENSES = [...LICENSES_V1, 'Bucket Plan Certification'];

// v2 collapses the two Frontstage Advisory rows into one titled by role, and
// simplifies Business Development to B2B / B2C / M&A.
const LANES = LANES_V1.map((g) => {
  if (g.lane === 'Financial Services') {
    return { lane: g.lane, subs: [
      'Frontstage Advisory (Sr Wealth Managers / Wealth Managers)',
      'Backstage Advisory Support (CSA / Paraplanner)',
      'Transactional Services (Tax Prep, Tax Planning, Insurance, Annuity, Social Security)',
    ] };
  }
  if (g.lane === 'Business Development') {
    return { lane: g.lane, subs: ['B2B', 'B2C', 'M&A Sales'] };
  }
  return g;
});

// Colors mirror the original Activity Snapshot zones (green / blue / amber / red).
const QUADRANT_ZONES = [
  { key: 'ua', title: 'Unique Ability Activities', color: '#2E7D32', bg: '#E8F5E9', placeholder: 'List your Unique Ability activities here...', desc: ['Superior skill', 'Passion and energy', 'Never-ending improvement', 'You would do this for free'] },
  { key: 'ex', title: 'Excellent Activities', color: '#1565C0', bg: '#E3F2FD', placeholder: 'List your Excellent activities here...', desc: ['Superior skill', 'No passion (feels like work)', 'Known for it / reputation', 'Others depend on you for it'] },
  { key: 'co', title: 'Competent Activities', color: '#F57F17', bg: '#FFF8E1', placeholder: 'List your Competent activities here...', desc: ['Meet minimum standards', 'Feels competitive / anxious', 'Repetitive, no growth', 'Someone else could do it better'] },
  { key: 'ic', title: 'Incompetent Activities', color: '#B71C1C', bg: '#FFEBEE', placeholder: 'List your Incompetent activities here...', desc: ['Failure-prone', 'Causes frustration and stress', 'Creates conflict', 'Avoidance and procrastination'] },
];

// The questionnaire as data. `blocks` render in order; results skip 'note'.
// ── v1, FROZEN 2026-08-20 ────────────────────────────────────────────────────
// Render-only. Kept so the submissions collected before v2 still display with the
// questions and options their authors actually saw. Do not edit; add to v2 below.
export const ROLE_ALIGNMENT_SCHEMA_V1 = [
  {
    num: 'Section 1', title: 'Employee Information',
    blocks: [
      { kind: 'fields', fields: [
        { name: 'full_name', label: 'Full Name', type: 'text', col: 12 },
        { name: 'current_firm', label: 'Current Firm', type: 'text', col: 6 },
        { name: 'current_title', label: 'Current Title', type: 'text', col: 6 },
        { name: 'years_industry', label: 'Total Years in Industry', type: 'text', col: 6 },
        // Auto-filled with today's date and locked — the author keeps the field,
        // but the employee doesn't hand-enter it.
        { name: 'date_completed', label: 'Date Completed', type: 'text', col: 6, autoDate: true },
      ] },
    ],
  },
  {
    num: 'Section 2', title: 'Career Path Selection',
    blocks: [
      { kind: 'note', text: 'The two career tracks and their rungs are described in the Prosperity Career Path Guide.' },
      { kind: 'subheader', text: '2A. My Current Path' },
      { kind: 'fields', fields: [
        { name: 'current_path', label: 'My Current Path', type: 'radio', hideLabel: true,
          options: ['Advisor Career Path', 'Operations Career Path', 'Hybrid / Unclear'] },
      ] },
      { kind: 'subheader', text: '2B. My Desired Path Going Forward' },
      { kind: 'fields', fields: [
        { name: 'desired_path', label: 'My Desired Path', type: 'radio', hideLabel: true,
          options: ['Advisor Career Path', 'Operations Career Path', 'I am open to either', 'I am considering switching paths'] },
        { name: 'switch_driver', label: 'If considering switching or unsure, what is driving that interest?', type: 'textarea', col: 12 },
      ] },
      { kind: 'subheader', text: '2C. Rung Alignment' },
      { kind: 'fields', fields: [
        { name: 'rung_today', label: 'Where I am today', type: 'select', options: RUNGS_V1, col: 6 },
        { name: 'rung_aspire', label: 'Where I aspire to be in 2-3 years', type: 'select', options: RUNGS_V1, col: 6 },
      ] },
      { kind: 'subheader', text: '2D. People Leadership Interest' },
      { kind: 'fields', fields: [
        { name: 'leadership_interest', label: 'People Leadership Interest', type: 'radio', hideLabel: true,
          options: ['I want to manage people and build a team', 'I am open to it with the right development', 'I prefer an individual contributor path'] },
      ] },
    ],
  },
  {
    num: 'Section 3', title: 'Unique Ability Discovery',
    blocks: [
      { kind: 'callout', variant: 'blue', title: 'About This Section', paragraphs: [
        'Everyone has a Unique Ability: the intersection of a superior skill that you are passionate about, that gives you energy, and that drives never-ending improvement.',
        'You will complete three exercises: (1) an Activity Inventory, (2) an Activity Snapshot categorizing activities into four zones, and (3) a reflection on how to shift more time toward your Unique Ability at Prosperity.',
      ] },
      { kind: 'subheader', text: '3A. Activity Inventory' },
      { kind: 'note', text: 'List up to 10 things you do in your current role. Be specific: run reports, meet with clients, build financial plans, process paperwork, mentor colleagues, etc.' },
      { kind: 'inventory', name: 'activity_inventory', label: 'Activity Inventory', count: 10 },
      { kind: 'subheader', text: '3B. Activity Snapshot' },
      { kind: 'note', text: 'Categorize each activity into one of four zones. Write activity numbers or names in each box. The four percentages should total 100%.' },
      { kind: 'quadrant', zones: QUADRANT_ZONES },
      { kind: 'callout', variant: 'green', title: 'Reflection', paragraphs: [
        'Looking at your Activity Snapshot, what patterns do you notice? What would change if you could spend 80% or more of your time in your Unique Ability zone?',
      ] },
      { kind: 'fields', fields: [
        { name: 'ua_reflection', label: 'Reflection', type: 'textarea', hideLabel: true, col: 12 },
      ] },
    ],
  },
  {
    num: 'Section 4', title: 'Accountability Chart',
    blocks: [
      { kind: 'note', text: 'Check the lane(s) that interest you, then check specific sub-functions. You may select across multiple lanes. Selecting the lane and sub-function of your current role is perfectly fine; this is about where you want to contribute, whether that is right where you are today or somewhere new.' },
      { kind: 'lanes', name: 'accountability', groups: LANES_V1 },
      { kind: 'fields', fields: [
        { name: 'lanes_appeal', label: 'Why do these lanes appeal to you? How do they connect to your Unique Ability?', type: 'textarea', col: 12 },
      ] },
    ],
  },
  {
    num: 'Section 5', title: 'Skills and Technology',
    blocks: [
      { kind: 'fields', fields: [
        { name: 'highest_degree', label: 'Highest Degree Earned', type: 'text', col: 6 },
        { name: 'field_of_study', label: 'Field of Study', type: 'text', col: 6 },
      ] },
      { kind: 'subheader', text: 'Licenses and Designations' },
      { kind: 'fields', fields: [
        { name: 'licenses', label: 'Licenses and Designations', type: 'checkgroup', hideLabel: true, options: LICENSES_V1 },
        { name: 'licenses_other', label: 'Other', type: 'text', col: 12 },
      ] },
      { kind: 'subheader', text: 'Technology Proficiency' },
      { kind: 'note', text: 'Rate each system on a 1 to 5 scale where 1 = No Experience and 5 = Expert.' },
      { kind: 'ratings', name: 'tech_proficiency', rows: TECH_SYSTEMS_V1 },
      { kind: 'fields', fields: [
        { name: 'skills_develop', label: 'What skills or knowledge areas would you like to develop?', type: 'textarea', col: 12 },
      ] },
    ],
  },
  {
    num: 'Section 6', title: 'Transition Priorities and Closing',
    blocks: [
      { kind: 'note', text: 'Use your Activity Snapshot (3B) to guide your answers. Competent and Incompetent activities are prime candidates for shedding. Unique Ability and Excellent activities are what you should lean into.' },
      { kind: 'subheader', text: '6A. Hats I Want to Keep (Unique Ability and Excellent)' },
      { kind: 'fields', fields: [
        { name: 'hats_keep', label: '', type: 'textarea', col: 12, placeholder: 'List the responsibilities you are passionate about and want to continue...' },
      ] },
      { kind: 'subheader', text: '6B. Hats I Want to Shed (Competent and Incompetent)' },
      { kind: 'fields', fields: [
        { name: 'hats_shed', label: '', type: 'textarea', col: 12, placeholder: 'List responsibilities to hand off. Note the zone (Competent or Incompetent) and why...' },
      ] },
      { kind: 'subheader', text: '6C. Hats I Would Like to Pick Up' },
      { kind: 'fields', fields: [
        { name: 'hats_pickup', label: '', type: 'textarea', col: 12, placeholder: 'Responsibilities you believe could become part of your Unique Ability...' },
      ] },
      { kind: 'subheader', text: '6D. Anything Else' },
      { kind: 'fields', fields: [
        { name: 'anything_else', label: 'Questions or concerns about the transition? Anything else the Advisor Success team should know?', type: 'textarea', col: 12 },
      ] },
      { kind: 'callout', variant: 'orange', title: 'Next Steps', paragraphs: [
        'Thank you for completing this questionnaire. Your Advisor Success team member will review your responses and schedule a Role Alignment Conversation to discuss your goals, explore potential seats, and build a personalized transition plan. Depending on your path selection, you may receive a short follow-up addendum before that conversation.',
      ] },
      { kind: 'fields', fields: [
        { name: 'employee_signature', label: 'Employee Signature', type: 'text', col: 6 },
        { name: 'signature_date', label: 'Date', type: 'text', col: 6, autoDate: true },
      ] },
    ],
  },
];


// ── v2, ACTIVE ───────────────────────────────────────────────────────────────
// Replaces v1 in place: same form_key, same page, so there is one Role Alignment
// form rather than two. New submissions are tagged `_form_version: 2` and the
// results view picks the matching schema, which is what lets v1 stay readable.
export const ROLE_ALIGNMENT_SCHEMA = [
  {
    num: 'Section 1', title: 'Employee Information',
    blocks: [
      { kind: 'fields', fields: [
        { name: 'full_name', label: 'Full Name', type: 'text', col: 12 },
        { name: 'current_firm', label: 'Current Firm', type: 'text', col: 6 },
        { name: 'current_title', label: 'Current Title', type: 'text', col: 6 },
        { name: 'years_industry', label: 'Total Years in Industry', type: 'text', col: 6 },
        { name: 'office_location', label: 'Office Location', type: 'text', col: 6 },
        { name: 'date_completed', label: 'Date Completed', type: 'text', col: 6, autoDate: true },
      ] },
    ],
  },
  {
    num: 'Section 2', title: 'Career Path Selection',
    blocks: [
      { kind: 'note', text: 'The two career paths and their rungs are described in the Prosperity Career Path Guide.' },
      // Opens the guide in a new tab so a part-filled form is never lost. The
      // form also autosaves to localStorage, so this is belt and braces.
      { kind: 'guide', label: 'Open the Career Path Guide', href: '/career-path-guide.html' },
      { kind: 'subheader', text: '2A. My Current Path' },
      { kind: 'fields', fields: [
        { name: 'current_path', label: 'My Current Path', type: 'radio', hideLabel: true,
          options: ['Advisor Career Path', 'Operations Career Path', 'Hybrid / Unclear'] },
      ] },
      { kind: 'subheader', text: '2B. My Desired Path Going Forward' },
      { kind: 'fields', fields: [
        { name: 'desired_path', label: 'My Desired Path Going Forward', type: 'radio', hideLabel: true,
          options: ['Advisor Career Path', 'Operations Career Path', 'I am open to either', 'I am considering switching paths'] },
        { name: 'switch_driver', label: 'If considering switching or unsure, what is driving that interest?', type: 'textarea', col: 12 },
      ] },
      { kind: 'subheader', text: '2C. Rung Alignment' },
      { kind: 'fields', fields: [
        { name: 'rung_today', label: 'Where I am today', type: 'select', options: RUNGS, col: 6 },
        { name: 'rung_aspire', label: 'Where I aspire to be in 2-3 years', type: 'select', options: RUNGS, col: 6 },
      ] },
      { kind: 'subheader', text: '2D. People Leadership Interest' },
      { kind: 'fields', fields: [
        { name: 'leadership_interest', label: 'People Leadership Interest', type: 'radio', hideLabel: true,
          options: ['I want to manage people and build a team', 'I am open to it with the right development', 'I prefer an individual contributor path'] },
      ] },
    ],
  },
  {
    num: 'Section 3', title: 'Unique Ability Discovery',
    blocks: [
      { kind: 'callout', variant: 'blue', title: 'About This Section', paragraphs: [
        'Everyone has a Unique Ability: the intersection of a superior skill that you are passionate about, that gives you energy, and that drives never-ending improvement.',
        'You will complete three exercises: (1) an Activity Inventory, (2) an Activity Snapshot categorizing activities into four zones, and (3) a reflection on how to shift more time toward your Unique Ability at Prosperity.',
      ] },
      { kind: 'subheader', text: '3A. Activity Inventory' },
      { kind: 'note', text: 'List up to 10 things you do in your current role. Be specific: run reports, meet with clients, build financial plans, process paperwork, mentor colleagues, etc.' },
      { kind: 'inventory', name: 'activity_inventory', label: 'Activity Inventory', count: 10 },
      { kind: 'subheader', text: '3B. Activity Snapshot' },
      { kind: 'note', text: 'Categorize each activity into one of four zones. Write the activity name in each box. The four percentages should total 100%.' },
      { kind: 'quadrant', zones: QUADRANT_ZONES },
      { kind: 'callout', variant: 'green', title: 'Reflection', paragraphs: [
        'Looking at your Activity Snapshot, what patterns do you notice? What would change if you could spend 80% or more of your time in your Unique Ability zone?',
      ] },
      { kind: 'fields', fields: [
        { name: 'ua_reflection', label: 'Reflection', type: 'textarea', hideLabel: true, col: 12, rows: 4 },
      ] },
    ],
  },
  {
    num: 'Section 4', title: 'Accountability Chart',
    blocks: [
      { kind: 'note', text: 'Check the lane(s) that interest you, then check specific seats. You may select across multiple lanes. Selecting the lane and seat of your current role is perfectly fine; this is about where you want to contribute, whether that is right where you are today or somewhere new.' },
      { kind: 'lanes', name: 'accountability', groups: LANES },
      { kind: 'fields', fields: [
        { name: 'lanes_why', label: 'Why do these lanes appeal to you? How do they connect to your Unique Ability?', type: 'textarea', col: 12, rows: 4 },
      ] },
    ],
  },
  {
    num: 'Section 5', title: 'Skills and Technology',
    blocks: [
      { kind: 'fields', fields: [
        { name: 'highest_degree', label: 'Highest Degree Earned', type: 'text', col: 6 },
        { name: 'field_of_study', label: 'Field of Study', type: 'text', col: 6 },
      ] },
      { kind: 'subheader', text: 'Licenses and Designations' },
      { kind: 'fields', fields: [
        { name: 'licenses', label: 'Licenses and Designations', type: 'checkgroup', hideLabel: true, options: LICENSES },
        { name: 'licenses_other', label: 'Other', type: 'text', col: 12 },
      ] },
      { kind: 'subheader', text: 'Technology Proficiency' },
      { kind: 'note', text: 'Rate each system on a 1 to 5 scale where 1 = No Experience and 5 = Expert.' },
      { kind: 'ratings', name: 'tech_proficiency', rows: TECH_SYSTEMS },
      { kind: 'fields', fields: [
        { name: 'tech_other', label: 'If you rated "Other" above, which system?', type: 'text', col: 12 },
      ] },
    ],
  },
  {
    num: 'Section 6', title: 'Transition Priorities and Closing',
    blocks: [
      { kind: 'note', text: 'Use your Activity Snapshot (3B) to guide your answers. Competent and Incompetent activities are prime candidates for shedding. Unique Ability and Excellent activities are what you should lean into.' },
      { kind: 'subheader', text: '6A. Hats I Want to Keep (Unique Ability and Excellent)' },
      { kind: 'fields', fields: [
        { name: 'hats_keep', label: 'Hats I Want to Keep', type: 'textarea', hideLabel: true, col: 12, rows: 4, placeholder: 'List the responsibilities you are passionate about and want to continue...' },
      ] },
      { kind: 'subheader', text: '6B. Hats I Want to Shed (Competent and Incompetent)' },
      { kind: 'fields', fields: [
        { name: 'hats_shed', label: 'Hats I Want to Shed', type: 'textarea', hideLabel: true, col: 12, rows: 4, placeholder: 'List responsibilities to hand off. Note the zone (Competent or Incompetent) and why...' },
      ] },
      { kind: 'subheader', text: '6C. Hats I Would Like to Pick Up' },
      { kind: 'fields', fields: [
        { name: 'hats_pickup', label: 'Hats I Would Like to Pick Up', type: 'textarea', hideLabel: true, col: 12, rows: 4, placeholder: 'Responsibilities you believe could become part of your Unique Ability...' },
      ] },
      { kind: 'subheader', text: '6D. Anything Else' },
      { kind: 'fields', fields: [
        { name: 'anything_else', label: 'Any further questions or comments?', type: 'textarea', col: 12, rows: 3 },
      ] },
      { kind: 'callout', variant: 'orange', title: 'Next Steps', paragraphs: [
        'Thank you for completing this questionnaire. Your responses will be reviewed and a Role Alignment Conversation will be scheduled to discuss your goals and explore potential seats.',
      ] },
      { kind: 'fields', fields: [
        { name: 'employee_signature', label: 'Employee Signature', type: 'text', col: 6 },
        { name: 'signature_date', label: 'Date', type: 'text', col: 6, autoDate: true },
      ] },
    ],
  },
];

// Stamped into every new submission so the results view can pick the schema that
// matches what the person was asked. Legacy rows have no marker and fall back to
// v1 — see schemaForVersion below.
export const ROLE_ALIGNMENT_VERSION = 2;

export function schemaForVersion(version) {
  return Number(version) >= 2 ? ROLE_ALIGNMENT_SCHEMA : ROLE_ALIGNMENT_SCHEMA_V1;
}

const styleTag = `
.ra-form { max-width: 860px; margin: 0 auto; color: ${brand.text}; }
.ra-form .ra-section { background:#fff; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,.08); margin-bottom:20px; overflow:hidden; }
.ra-form .ra-section-header { background:${brand.navy}; padding:12px 20px; }
.ra-form .ra-section-header .num { color:${brand.orange}; font-weight:700; font-size:14px; margin-right:8px; }
.ra-form .ra-section-header .title { color:#fff; font-weight:600; font-size:14px; }
.ra-form .ra-body { padding:20px; }
.ra-form .ra-subheader { font-size:15px; font-weight:600; color:${brand.blue}; border-bottom:2px solid ${brand.orange}; padding-bottom:6px; margin:20px 0 12px; }
.ra-form .ra-note { font-size:13px; color:${brand.textLight}; font-style:italic; margin-bottom:12px; }
.ra-form label.ra-label { display:block; font-size:13px; font-weight:600; color:${brand.navy}; margin-bottom:4px; }
.ra-form input[type=text], .ra-form input[type=number], .ra-form textarea, .ra-form select {
  width:100%; border:1px solid ${brand.medGray}; border-radius:4px; padding:8px 12px; font-size:14px; font-family:inherit; background:#fff; }
.ra-form textarea { resize:vertical; min-height:80px; }
.ra-form .ra-grid { display:grid; grid-template-columns:repeat(12,1fr); gap:12px 16px; }
.ra-form .ra-radio, .ra-form .ra-check { display:flex; align-items:flex-start; gap:8px; padding:5px 0; font-size:14px; cursor:pointer; }
.ra-form .ra-checkgrid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:2px 16px; }
.ra-form .ra-inv { display:grid; grid-template-columns:1fr 1fr; gap:8px 16px; }
.ra-form .ra-inv-col { display:flex; flex-direction:column; gap:8px; }
.ra-form .ra-inv-row { display:flex; align-items:center; gap:8px; }
.ra-form .ra-inv-num { width:26px; height:26px; flex:0 0 26px; border-radius:50%; background:#f0f2f5; color:${brand.navy}; font-weight:700; font-size:12px; display:flex; align-items:center; justify-content:center; }
.ra-form .ra-quad { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.ra-form .ra-quad-box { border:2px solid ${brand.blue}; border-radius:8px; padding:14px; }
.ra-form .ra-quad-title { font-weight:700; font-size:14px; color:#fff; background:${brand.blue}; padding:4px 8px; border-radius:4px; margin-bottom:8px; }
.ra-form .ra-quad-desc { font-size:12px; color:#555; margin:0 0 10px 18px; }
.ra-form .ra-callout { border-radius:8px; padding:18px 22px; margin:16px 0; border-left:5px solid; }
.ra-form .ra-callout.blue { background:#E8F0F8; border-color:#0060A0; }
.ra-form .ra-callout.green { background:#E8F5E9; border-color:#2E7D32; }
.ra-form .ra-callout.orange { background:#FDF0E0; border-color:#002040; }
.ra-form .ra-callout h3 { font-size:15px; color:#002040; margin:0 0 8px; }
.ra-form .ra-callout p { font-size:13px; color:#444; margin:0 0 6px; }
.ra-form .ra-callout p:last-child { margin-bottom:0; }
.ra-form .ra-quad-pct { display:flex; align-items:center; justify-content:flex-end; gap:6px; margin-top:8px; font-size:13px; font-weight:600; }
.ra-form .ra-quad-pct input { width:64px; text-align:center; }
.ra-form .ra-rating { width:100%; border-collapse:collapse; font-size:13px; }
.ra-form .ra-rating th { background:${brand.navy}; color:#fff; padding:8px; text-align:center; }
.ra-form .ra-rating th:first-child { text-align:left; }
.ra-form .ra-rating td { padding:8px; border:1px solid ${brand.medGray}; text-align:center; }
.ra-form .ra-rating td:first-child { text-align:left; }
.ra-form .ra-lane { width:100%; border-collapse:collapse; font-size:13px; margin-bottom:8px; }
.ra-form .ra-lane td { padding:10px 12px; border:1px solid ${brand.medGray}; vertical-align:top; }
.ra-form .ra-lane .ra-lane-name { font-weight:600; color:${brand.navy}; }
.ra-form .ra-pct-total { text-align:center; font-size:13px; margin:12px 0; }
.ra-form .ra-purpose { background:${brand.ltBlue}; border-left:4px solid ${brand.blue}; padding:18px 22px; border-radius:0 8px 8px 0; margin-bottom:20px; font-size:14px; color:#444; }
.ra-form .ra-purpose strong { color:${brand.navy}; }
.ra-form .ra-purpose p { margin:0 0 10px; }
.ra-form .ra-purpose p:last-child { margin-bottom:0; }
@media(max-width:640px){ .ra-form .ra-quad, .ra-form .ra-inv { grid-template-columns:1fr; } .ra-form .ra-checkgrid { grid-template-columns:1fr 1fr; } }
`;

// Intro/purpose blurb shown at the top of the questionnaire (blue box), carried
// over from the original form's cover.
function PurposeBlock() {
  return (
    <div className="ra-purpose">
      <p><strong>Purpose</strong></p>
      <p>
        This questionnaire helps us understand your experience, interests, and aspirations as you join
        Prosperity. Many people in our industry wear multiple hats. This is your opportunity to share which
        responsibilities energize you, which ones you would like to step away from, and where you see yourself
        growing.
      </p>
      <p>
        Your responses will guide a one-on-one Role Alignment Conversation with your Advisor Success team
        member. There are no wrong answers.
      </p>
      <p>
        Before you begin, review the{' '}
        <a href="/career-path-guide.html" target="_blank" rel="noopener noreferrer"
           style={{ color: brand.blue, fontWeight: 600 }}>Prosperity Career Path Guide</a>{' '}
        so the two career paths and rungs referenced below are familiar. This questionnaire takes
        about 30 minutes.
      </p>
    </div>
  );
}

function colSpan(col) { return { gridColumn: `span ${col || 12}` }; }

// ── Fillable form ──────────────────────────────────────────────────────────────
// Field names flagged `autoDate` in the schema — pre-filled with today's date
// and rendered read-only.
//
// Read off the schema actually being rendered, not the default one: a form that
// hides a section (the Managerial Assessment hides its signature block) must not
// stamp a date into a field nobody was shown, or the results view reports a
// signature date for a signature that was never asked for.
function autoDateFields(schema) {
  return schema.flatMap((s) => s.blocks)
    .filter((b) => b.kind === 'fields')
    .flatMap((b) => b.fields)
    .filter((f) => f.autoDate)
    .map((f) => f.name);
}

// The renderer is schema-driven, so a different schema makes a different form.
// The Managerial Assessment passes its own; everything else here is shared.
export default function RoleAlignmentForm({ mode = 'fill', onSubmit, submitting, initial,
                                            storageKey, schema = ROLE_ALIGNMENT_SCHEMA,
                                            version = ROLE_ALIGNMENT_VERSION,
                                            purpose = null, confirmText = null,
                                            initialSavedAt = null, onResponsesChange = null,
                                            draftStatus = null, onSaveLater = null,
                                            onDiscardDraft = null }) {
  const readOnly = mode === 'preview';
  // Drafts are only persisted for an actively-filled form, never the Tech preview.
  const persist = !readOnly && Boolean(storageKey);

  const [responses, setResponses] = useState(() => {
    // Precedence, in order: `initial` (the server draft plus directory prefill,
    // resolved by the parent), then the LOCAL draft but only when it is newer.
    //
    // That comparison is the whole point of storing a timestamp locally. The
    // local copy is written on every keystroke while the server copy is written
    // on a debounce, so locally-newer is the normal case on the same machine and
    // must win. But a draft continued on a second device has an older local copy
    // (or none), and letting that overwrite the server's would silently revert
    // work done elsewhere — the bug that makes people stop trusting a draft.
    const today = new Date().toLocaleDateString();
    let base = { ...(initial || {}) };
    if (persist) {
      try {
        const raw = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
        if (raw && typeof raw === 'object') {
          // v2 is { saved_at, responses }; a bare object is a pre-v2 draft, which
          // has no timestamp and so is treated as older than anything on the
          // server. Existing half-finished forms therefore survive this change.
          const local = raw.responses && typeof raw.responses === 'object' ? raw.responses : raw;
          const localAt = raw.saved_at ? Date.parse(raw.saved_at) : 0;
          const serverAt = initialSavedAt ? Date.parse(initialSavedAt) : 0;
          if (!serverAt || localAt > serverAt) base = { ...base, ...local };
        }
      } catch { /* ignore a corrupt draft */ }
    }
    for (const name of autoDateFields(schema)) base[name] = today;
    return base;
  });
  const set = (name, value) => setResponses((r) => ({ ...r, [name]: value }));

  // Two-layer autosave. localStorage is written on every change (instant, and
  // survives a crash or a dropped connection between server saves); the parent
  // debounces the server write, which is what makes the draft available on
  // another day and another device. The parent clears both on a successful
  // submit.
  useEffect(() => {
    if (!persist) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({
        saved_at: new Date().toISOString(), responses,
      }));
    } catch { /* storage full / unavailable — non-fatal */ }
  }, [responses, persist, storageKey]);

  // Deliberately skips the first run. On mount `responses` is just the seeded
  // state (prefilled name, today's date), so notifying then would create a
  // server draft for anyone who merely opened the form — and greet them next
  // time with "continuing your saved answers" when they never typed a word.
  const notifyChange = onResponsesChange;
  const notifiedOnce = useRef(false);
  useEffect(() => {
    if (!persist || !notifyChange) return;
    if (!notifiedOnce.current) { notifiedOnce.current = true; return; }
    notifyChange(responses);
  }, [responses, persist, notifyChange]);

  const pctTotal = useMemo(() => QUADRANT_ZONES
    .reduce((sum, z) => sum + (parseInt(responses[`${z.key}_pct`], 10) || 0), 0), [responses]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (!window.confirm(confirmText || 'Submit your Role Alignment Questionnaire? You won’t be able to edit it after submitting.')) return;
    // Stamped so the results view renders this submission against the question
    // set it was actually written against, not whatever the form says next year.
    onSubmit?.({ ...responses, _form_version: version });
  };

  const toggleInArray = (name, value) => {
    const cur = Array.isArray(responses[name]) ? responses[name] : [];
    set(name, cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]);
  };

  const renderField = (f) => {
    const val = responses[f.name];
    const common = { disabled: readOnly };
    const showLabel = f.label && !f.hideLabel;
    if (f.type === 'text') {
      return (
        <div key={f.name} style={colSpan(f.col)}>
          {showLabel && <label className="ra-label">{f.label}</label>}
          <input
            type="text"
            value={val || ''}
            placeholder={f.placeholder}
            onChange={(e) => set(f.name, e.target.value)}
            {...common}
            disabled={readOnly || Boolean(f.autoDate)}
          />
        </div>
      );
    }
    if (f.type === 'textarea') {
      return (
        <div key={f.name} style={colSpan(f.col)}>
          {showLabel && <label className="ra-label">{f.label}</label>}
          <textarea value={val || ''} placeholder={f.placeholder} onChange={(e) => set(f.name, e.target.value)} {...common} />
        </div>
      );
    }
    if (f.type === 'select') {
      return (
        <div key={f.name} style={colSpan(f.col)}>
          {showLabel && <label className="ra-label">{f.label}</label>}
          <select value={val || ''} onChange={(e) => set(f.name, e.target.value)} {...common}>
            <option value="">—</option>
            {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      );
    }
    if (f.type === 'radio') {
      return (
        <div key={f.name} style={colSpan(12)}>
          {!f.hideLabel && <label className="ra-label">{f.label}</label>}
          {f.options.map((o) => (
            <label key={o} className="ra-radio">
              <input type="radio" name={f.name} checked={val === o} onChange={() => set(f.name, o)} disabled={readOnly} />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
    }
    if (f.type === 'checkgroup') {
      const arr = Array.isArray(val) ? val : [];
      return (
        <div key={f.name} style={colSpan(12)}>
          {!f.hideLabel && <label className="ra-label">{f.label}</label>}
          <div className="ra-checkgrid">
            {f.options.map((o) => (
              <label key={o} className="ra-check">
                <input type="checkbox" checked={arr.includes(o)} onChange={() => toggleInArray(f.name, o)} disabled={readOnly} />
                <span>{o}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderBlock = (block, i) => {
    if (block.kind === 'subheader') return <div key={i} className="ra-subheader">{block.text}</div>;
    if (block.kind === 'note') return <p key={i} className="ra-note">{block.text}</p>;
    if (block.kind === 'guide') {
      // New tab: the guide is reference material read alongside a part-filled form.
      return (
        <p key={i} style={{ margin: '0 0 14px' }}>
          <a href={block.href} target="_blank" rel="noopener noreferrer"
             style={{ display: 'inline-block', padding: '7px 14px', borderRadius: 5,
                      background: brand.blue, color: '#fff', fontSize: 13,
                      fontWeight: 600, textDecoration: 'none' }}>
            {block.label} &nbsp;&#8599;
          </a>
        </p>
      );
    }
    if (block.kind === 'callout') {
      return (
        <div key={i} className={`ra-callout ${block.variant}`}>
          {block.title && <h3>{block.title}</h3>}
          {block.paragraphs.map((p, pi) => <p key={pi}>{p}</p>)}
        </div>
      );
    }
    if (block.kind === 'fields') {
      return <div key={i} className="ra-grid">{block.fields.map(renderField)}</div>;
    }
    if (block.kind === 'inventory') {
      const arr = Array.isArray(responses[block.name]) ? responses[block.name] : [];
      const renderRow = (idx) => (
        <div key={idx} className="ra-inv-row">
          <span className="ra-inv-num">{idx + 1}</span>
          <input
            type="text"
            value={arr[idx] || ''}
            disabled={readOnly}
            onChange={(e) => {
              const next = [...arr];
              next[idx] = e.target.value;
              set(block.name, next);
            }}
          />
        </div>
      );
      // Two explicit columns so the numbering runs DOWN the first column, then
      // down the second (1–5 left, 6–10 right) rather than left-to-right.
      const half = Math.ceil(block.count / 2);
      const firstCol = Array.from({ length: half }, (_, k) => k);
      const secondCol = Array.from({ length: block.count - half }, (_, k) => half + k);
      return (
        <div key={i} className="ra-inv">
          <div className="ra-inv-col">{firstCol.map(renderRow)}</div>
          <div className="ra-inv-col">{secondCol.map(renderRow)}</div>
        </div>
      );
    }
    if (block.kind === 'quadrant') {
      return (
        <div key={i}>
          <div className="ra-quad">
            {block.zones.map((z) => (
              <div key={z.key} className="ra-quad-box" style={{ borderColor: z.color, background: z.bg }}>
                <div className="ra-quad-title" style={{ background: z.color }}>{z.title}</div>
                <ul className="ra-quad-desc">{z.desc.map((d) => <li key={d}>{d}</li>)}</ul>
                <textarea
                  placeholder={z.placeholder}
                  value={responses[`${z.key}_activities`] || ''}
                  disabled={readOnly}
                  onChange={(e) => set(`${z.key}_activities`, e.target.value)}
                  style={{ minHeight: 60, fontSize: 12 }}
                />
                <div className="ra-quad-pct" style={{ color: z.color }}>
                  Estimated % of time:
                  <input
                    type="number" min="0" max="100"
                    value={responses[`${z.key}_pct`] || ''}
                    disabled={readOnly}
                    onChange={(e) => set(`${z.key}_pct`, e.target.value)}
                  />%
                </div>
              </div>
            ))}
          </div>
          <div className="ra-pct-total" style={{ color: pctTotal === 100 ? '#2E7D32' : (pctTotal > 100 ? '#B71C1C' : '#F57F17') }}>
            {pctTotal > 0 && (pctTotal === 100 ? 'Total: 100% ✓' : `Total: ${pctTotal}% ${pctTotal > 100 ? '(exceeds 100%)' : '(should equal 100%)'}`)}
          </div>
        </div>
      );
    }
    if (block.kind === 'ratings') {
      const cur = responses[block.name] || {};
      return (
        <table key={i} className="ra-rating">
          <thead>
            <tr>
              <th>{block.rowHeader || 'Tool / System'}</th>
              {(block.scale || [1, 2, 3, 4, 5]).map((n) => <th key={n}>{n}</th>)}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row) => (
              <tr key={row}>
                <td>{row}</td>
                {(block.scale || [1, 2, 3, 4, 5]).map((n) => (
                  <td key={n}>
                    <input
                      type="radio" name={`${block.name}_${row}`}
                      checked={String(cur[row]) === String(n)}
                      disabled={readOnly}
                      onChange={() => set(block.name, { ...cur, [row]: String(n) })}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (block.kind === 'lanes') {
      const cur = responses[block.name] || { lanes: [], subs: [] };
      const lanes = cur.lanes || [];
      const subs = cur.subs || [];
      const toggleLane = (lane) => set(block.name, {
        ...cur, lanes: lanes.includes(lane) ? lanes.filter((l) => l !== lane) : [...lanes, lane],
      });
      const toggleSub = (sub) => set(block.name, {
        ...cur, subs: subs.includes(sub) ? subs.filter((s) => s !== sub) : [...subs, sub],
      });
      return (
        <table key={i} className="ra-lane">
          <tbody>
            <tr><td className="ra-lane-name">Lane</td><td className="ra-lane-name">Sub-Functions (check areas of interest)</td></tr>
            {block.groups.map((g) => (
              <tr key={g.lane}>
                <td>
                  <label className="ra-check">
                    <input type="checkbox" checked={lanes.includes(g.lane)} onChange={() => toggleLane(g.lane)} disabled={readOnly} />
                    <span className="ra-lane-name">{g.lane}</span>
                  </label>
                </td>
                <td>
                  {g.subs.map((s) => (
                    <label key={s} className="ra-check" style={{ fontSize: 12 }}>
                      <input type="checkbox" checked={subs.includes(s)} onChange={() => toggleSub(s)} disabled={readOnly} />
                      <span>{s}</span>
                    </label>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return null;
  };

  return (
    <form className="ra-form" onSubmit={handleSubmit}>
      <style>{styleTag}</style>
      {purpose || <PurposeBlock />}
      {schema.map((section, si) => (
        <div key={si} className="ra-section">
          <div className="ra-section-header">
            <span className="num">{section.num}:</span><span className="title">{section.title}</span>
          </div>
          <div className="ra-body">
            {section.blocks.map(renderBlock)}
          </div>
        </div>
      ))}
      {!readOnly && (
        <div style={{ textAlign: 'center', padding: '8px 0 40px' }}>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
            {onSaveLater && (
              // An explicit button even though saving is automatic. People who
              // have typed for twenty minutes do not trust an invisible
              // mechanism, and "I pressed save" is what makes it safe to close
              // the tab. It saves the same draft the autosave does.
              <button
                type="button"
                onClick={onSaveLater}
                disabled={submitting}
                style={{ background: '#fff', color: brand.navy, border: `1px solid ${brand.navy}`, borderRadius: 6, padding: '12px 24px', fontSize: 15, fontWeight: 600 }}
              >
                Save &amp; finish later
              </button>
            )}
            <button
              type="submit"
              disabled={submitting}
              style={{ background: brand.navy, color: '#fff', border: 'none', borderRadius: 6, padding: '12px 32px', fontSize: 15, fontWeight: 600, opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
          {draftStatus && (
            <div style={{ marginTop: 10, fontSize: 13, color: brand.textLight }}>{draftStatus}</div>
          )}
          {onDiscardDraft && (
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={onDiscardDraft}
                style={{ background: 'none', border: 'none', color: brand.textLight, fontSize: 12, textDecoration: 'underline', padding: 0 }}
              >
                Discard my saved draft and start over
              </button>
            </div>
          )}
        </div>
      )}
    </form>
  );
}
RoleAlignmentForm.propTypes = {
  mode: PropTypes.oneOf(['fill', 'preview']),
  onSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  initial: PropTypes.object,
  storageKey: PropTypes.string,
  // Passing a schema makes this a different form — see ManagerialAssessmentForm.
  schema: PropTypes.array,
  version: PropTypes.number,
  purpose: PropTypes.node,
  confirmText: PropTypes.string,
  // When the server's draft was last written, so a local draft can be compared
  // against it rather than blindly overwriting newer work from another device.
  initialSavedAt: PropTypes.string,
  onResponsesChange: PropTypes.func,
  draftStatus: PropTypes.string,
  onSaveLater: PropTypes.func,
  onDiscardDraft: PropTypes.func,
};

// ── Read-only results renderer (Tech results tab) ──────────────────────────────
function fmtValue(value) {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ') || '—';
  if (typeof value === 'object') {
    // ratings ({system: '1'}) or lanes ({lanes, subs})
    if (Array.isArray(value.lanes) || Array.isArray(value.subs)) {
      const parts = [];
      if (value.lanes?.length) parts.push(`Lanes: ${value.lanes.join(', ')}`);
      if (value.subs?.length) parts.push(`Sub-functions: ${value.subs.join(', ')}`);
      return parts.join(' · ') || '—';
    }
    const entries = Object.entries(value).filter(([, v]) => v != null && v !== '');
    return entries.length ? entries.map(([k, v]) => `${k}: ${v}`).join(', ') : '—';
  }
  return String(value);
}

// Walks the schema to print each captured field with its human label.
/* One block of a schema → the [label, value] pairs it contributes.
 *
 * Extracted from the renderer so the CSV export walks the SAME logic rather than
 * a second copy of it. A download that quietly omits a question — because
 * someone added a block kind here and not there — is worse than no download,
 * and it is the kind of drift nobody notices until a review meeting.
 *
 * Blocks that carry no answer (notes, callouts, guides, subheaders) contribute
 * nothing; subheaders are handled by the renderer, which draws them.
 */
function blockItems(block, r) {
  if (block.kind === 'fields') return block.fields.map((f) => [f.label, r[f.name]]);
  if (block.kind === 'inventory') return [['Activity Inventory', r[block.name]]];
  if (block.kind === 'ratings') return [[block.label || 'Technology Proficiency', r[block.name]]];
  if (block.kind === 'lanes') return [['Accountability Chart', r[block.name]]];
  if (block.kind === 'quadrant') {
    return block.zones.flatMap((z) => [
      [z.title, r[`${z.key}_activities`]],
      [`${z.title} — % of time`, r[`${z.key}_pct`]],
    ]);
  }
  return [];
}

/* A submission as flat rows: section, question, answer.
 *
 * Backs the CSV download. Values go through the same `fmtValue` the page uses,
 * so a rating map or a lane list reads in the file exactly as it reads on
 * screen — the person printing and the person opening the spreadsheet should
 * not have to reconcile two renderings of the same answer.
 */
export function roleAlignmentRows(responses, schemaProp) {
  const r = responses || {};
  const schema = schemaProp || schemaForVersion(r._form_version);
  const rows = [];
  schema.forEach((section) => {
    section.blocks.forEach((block) => {
      blockItems(block, r).forEach(([label, value]) => {
        rows.push({ section: `${section.num}: ${section.title}`, question: label, answer: fmtValue(value) });
      });
    });
  });
  return rows;
}

export function RoleAlignmentResults({ responses, schema: schemaProp }) {
  const r = responses || {};
  // A submission is read against the schema it was written against. Rows from
  // before versioning have no marker and fall back to v1, so the questions and
  // options shown are the ones that person actually answered.
  const schema = schemaProp || schemaForVersion(r._form_version);
  return (
    <div className="ra-form" style={{ maxWidth: 820 }}>
      <style>{styleTag}</style>
      <div style={{ fontSize: 11, color: brand.textLight, marginBottom: 8 }}>
        Questionnaire version {Number(r._form_version) >= 2 ? '2' : '1 (before 20 Aug 2026)'}
      </div>
      {schema.map((section, si) => (
        <div key={si} className="ra-section">
          <div className="ra-section-header">
            <span className="num">{section.num}:</span><span className="title">{section.title}</span>
          </div>
          <div className="ra-body">
            {section.blocks.map((block, bi) => {
              if (block.kind === 'subheader') return <div key={bi} className="ra-subheader">{block.text}</div>;
              const items = blockItems(block, r);
              return items.map(([label, value], idx) => (
                <div key={`${bi}-${idx}`} style={{ marginBottom: 10 }}>
                  {label && <div style={{ fontSize: 12, fontWeight: 700, color: brand.textLight, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>}
                  <div style={{ fontSize: 14, color: brand.navy, whiteSpace: 'pre-wrap' }}>{fmtValue(value)}</div>
                </div>
              ));
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
RoleAlignmentResults.propTypes = { responses: PropTypes.object, schema: PropTypes.array };
